import React, { useRef } from "react";
import cx from "classnames";
import { IKImage } from "imagekitio-react";

import Card from "../card";
import { useDraft } from "../useDraft";
import { canEdit } from "src/pages/experiences/storyboardSettings/utils/permissions";
import { toastFailure } from "src/pages/experiences/storyboardSettings/utils/notifications";
import { SUPPORTED_TYPES } from "src/pages/experiences/constants/storyboard";
import { isAbsoluteUrl } from "src/pages/experiences/utils/cleverstorySitesettings";
import BrowserTabBar from "src/pages/experiences/storyboardSettings/components/browserTabBar";
import styles from "./favIconCard.module.scss";

const TARGET_SIZE = 196;

const resizeToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const src = reader.result as string;
      const img = new Image();
      img.onerror = () => reject(new Error("Could not load image"));
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = TARGET_SIZE;
        canvas.height = TARGET_SIZE;
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject(new Error("Canvas 2d unavailable"));
        ctx.clearRect(0, 0, TARGET_SIZE, TARGET_SIZE);
        ctx.drawImage(img, 0, 0, TARGET_SIZE, TARGET_SIZE);
        resolve(canvas.toDataURL("image/png"));
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
  });

const FavIconCard: React.FC = () => {
  const { draft, setField } = useDraft();
  const inputRef = useRef<HTMLInputElement>(null);
  const editable = canEdit(draft?.role);

  const favIconUrl =
    typeof draft?.favIcon === "string"
      ? (draft.favIcon as unknown as string)
      : (draft?.favIcon?.favIcon196x196 ??
        draft?.favIcon?.favIcon96x96 ??
        draft?.favIcon?.favIcon32x32 ??
        null);

  const isAbsoluteUrlFavIcon = !!favIconUrl && isAbsoluteUrl(favIconUrl);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editable) return;
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!SUPPORTED_TYPES.includes(file.type)) {
      toastFailure(`File type ${file.type} is not supported`);
      return;
    }

    try {
      const dataUrl = await resizeToDataUrl(file);
      setField("favIcon", dataUrl as any);
    } catch {
      toastFailure("Unable to process favicon");
    }
  };

  return (
    <Card
      title="Fav icon"
      description="Get a custom favicon to help visitors recognize your brand and to stand out in their browser tabs"
    >
      <div className={styles.row}>
        <div
          className={cx(styles.iconPreview, {
            [styles.hasIcon]: !!favIconUrl && favIconUrl.length > 0,
            [styles.disabled]: !editable,
          })}
          onClick={() => {
            if (!editable) return;
            if (!favIconUrl) inputRef.current?.click();
          }}
        >
          {favIconUrl &&
            favIconUrl.length > 0 &&
            (isAbsoluteUrlFavIcon ? (
              <img
                src={favIconUrl}
                alt="favicon"
                className={styles.iconPreviewImg}
              />
            ) : (
              <IKImage path={favIconUrl} alt="favicon" />
            ))}
          {!favIconUrl && (
            <i className={cx(styles.placeholder, "paperflite-upload")} />
          )}
          {editable && favIconUrl && favIconUrl.length > 0 && (
            <div className={styles.actionsOverlay}>
              <button
                type="button"
                className={styles.actionBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  inputRef.current?.click();
                }}
              >
                <i className="paperflite-edit" />
              </button>
              <span className={styles.divider} />
              <button
                type="button"
                className={styles.actionBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  setField("favIcon", null as any);
                }}
              >
                <i className="paperflite-delete" />
              </button>
            </div>
          )}
        </div>
        <input
          ref={inputRef}
          className={styles.hiddenInput}
          type="file"
          accept="image/png, image/jpeg, image/jpg, image/webp, image/svg+xml"
          onChange={handleUpload}
        />
        <BrowserTabBar
          icon={favIconUrl ?? ""}
          tabName={draft?.name ?? ""}
          tabURL={draft?.domain?.url ?? ""}
        />
      </div>
    </Card>
  );
};

export default FavIconCard;
